/**
 * Bare-domain and blacklist config for the chat message link-highlighter (see
 * ParseMentionsPipe). Explicit `https://`/`www.` links are always link-eligible (subject to
 * the blacklist below); a bare `word.tld` token (e.g. `google.com`) is only highlighted if its
 * TLD is on ALLOWED_TLDS — otherwise things like `e.g.`, `Node.js`, or `v1.2.3` would get
 * linkified as if they were domains.
 */

// Kept intentionally small for now — extend as needed. List compound TLDs (e.g. `com.ng`)
// alongside their parent (`ng`); registrableTld() checks the two-label suffix first so the
// compound one wins for hosts like `corpers.com.ng`.
export const ALLOWED_TLDS = [
  'com.ng',
  'org.ng',
  'gov.ng',
  'edu.ng',
  'ng',
  'com',
  'org',
  'net',
  'co',
  'io',
]

// TLDs that must never be highlighted as links, even if they'd otherwise be on ALLOWED_TLDS
// (e.g. a TLD known to be commonly abused for phishing). Empty by default — populate as needed.
export const BLACKLISTED_TLDS: string[] = []

// Specific registrable domains (e.g. `evil-example.com`) that must never be highlighted as
// links regardless of TLD. Subdomains of a blacklisted domain are blocked too.
export const BLACKLISTED_DOMAINS: string[] = []

const LABEL = '[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?'
const EXPLICIT_LINK_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi
// No lookbehind assertion here (unsupported on Safari < 16.4, which this app's browserslist
// doesn't exclude) — the '@' (email) exclusion is instead done as a plain post-match check.
const BARE_DOMAIN_REGEX = new RegExp(`${LABEL}(?:\\.${LABEL})+(?:/[^\\s]*)?`, 'g')
const TRAILING_PUNCTUATION_REGEX = /[.,!?:;)\]'"]+$/

export interface DetectedLink {
  index: number
  end: number
  text: string
  url: string
}

function trimTrailingPunctuation(raw: string): string {
  const trailing = raw.match(TRAILING_PUNCTUATION_REGEX)?.[0] ?? ''
  return trailing ? raw.slice(0, -trailing.length) : raw
}

/** Strips a scheme (if present) and any path/query/port, leaving just the host. */
function hostnameOf(token: string): string {
  return token.replace(/^https?:\/\//i, '').split(/[/?#:]/)[0].toLowerCase()
}

/** Finds the ALLOWED/BLACKLISTED entry matching `hostname`'s registrable TLD — checking the
 *  two-label suffix (e.g. `com.ng`) before falling back to the last label alone, so a compound
 *  TLD takes priority over its parent. */
function registrableTld(hostname: string, tlds: string[]): string | undefined {
  const labels = hostname.split('.')
  const twoLabelSuffix = labels.slice(-2).join('.')
  const oneLabelSuffix = labels.slice(-1).join('.')
  return tlds.find((tld) => tld === twoLabelSuffix) ?? tlds.find((tld) => tld === oneLabelSuffix)
}

function isBlacklisted(hostname: string): boolean {
  if (registrableTld(hostname, BLACKLISTED_TLDS)) return true
  return BLACKLISTED_DOMAINS.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`))
}

/** Finds every link-eligible substring in `text`, sorted by position and never overlapping.
 *  Explicit `https://`/`www.` links qualify outright (unless blacklisted); bare domains like
 *  `google.com` qualify only if their TLD is on ALLOWED_TLDS (and aren't blacklisted). */
export function findLinks(text: string): DetectedLink[] {
  const claimed: Array<[number, number]> = []
  const links: DetectedLink[] = []

  for (const match of text.matchAll(EXPLICIT_LINK_REGEX)) {
    const index = match.index ?? 0
    const value = trimTrailingPunctuation(match[0])
    if (!value) continue

    if (isBlacklisted(hostnameOf(value))) continue

    const end = index + value.length
    claimed.push([index, end])
    links.push({
      index,
      end,
      text: value,
      url: /^https?:\/\//i.test(value) ? value : `https://${value}`,
    })
  }

  for (const match of text.matchAll(BARE_DOMAIN_REGEX)) {
    const index = match.index ?? 0
    if (text[index - 1] === '@') continue // skip the domain half of an email address

    const value = trimTrailingPunctuation(match[0])
    if (!value) continue
    const end = index + value.length

    if (claimed.some(([cStart, cEnd]) => index < cEnd && end > cStart)) continue

    const hostname = hostnameOf(value)
    if (!registrableTld(hostname, ALLOWED_TLDS)) continue
    if (isBlacklisted(hostname)) continue

    claimed.push([index, end])
    links.push({ index, end, text: value, url: `https://${value}` })
  }

  return links.sort((a, b) => a.index - b.index)
}
